/* eslint-disable jsx-a11y/anchor-is-valid */
import { RetweetOutlined } from '@ant-design/icons';
import { faEdit, faExternalLinkAlt, faRedoAlt, faSyncAlt, faTimes, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UISref } from '@uirouter/react';
import { Badge, Button, notification, Popconfirm, Space, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { DeleteAction } from '@/components/table/actions';
import {
  PERMISSION_CREATE,
  PERMISSION_DELETE,
  PERMISSION_OBJ_DOCUMENT_IMPORT,
  PERMISSION_UPDATE,
} from '@/constants/permissions';
import { withPermissions } from '@/hocs/permission';
import { documentImportStore } from '@/stores';
import { showError } from '@/utils/common';
import { getTranslatedString } from '@/utils/intl';

// Client-only row for files dropped by the operator but not yet persisted as
// a `document_imports` row by the backend. They live in component state
// until `submitWaybillPdf` resolves; on success the local row is dropped
// and replaced by the server row; on failure it stays as `upload_failed`
// for the operator to see and dismiss.
export type LocalUploadRow = {
  _localId: string;
  fileName: string;
  // 'queued': waiting for a semaphore slot (no network activity yet)
  // 'uploading': POST in flight to /document-imports/waybill-pdf
  // 'upload_failed': POST rejected — row stays for the operator to dismiss
  _localStatus: 'queued' | 'uploading' | 'upload_failed';
  _error?: string;
  // Set on retryable 'upload_failed' rows; for a split page it is the only copy.
  _file?: File;
  // The multi-waybill PDF a split page came from.
  _splitFrom?: string;
};

export type DisplayRow = cyrian.api.documentImports.DocumentImport | LocalUploadRow;

export const isLocalRow = (r: DisplayRow): r is LocalUploadRow => (r as LocalUploadRow)._localId !== undefined;

const STATUS_COLORS: Record<
  cyrian.api.documentImports.DocumentImportStatus,
  'default' | 'processing' | 'success' | 'warning' | 'error'
> = {
  pending: 'processing',
  parsed: 'processing',
  persisted: 'success',
  review: 'warning',
  failed: 'error',
};

/** Used by the page to decide whether polling should keep firing. Local
 *  rows are component-only, so polling status is not affected by them. */
export const isActive = (status: cyrian.api.documentImports.DocumentImportStatus) =>
  status === 'pending' || status === 'parsed';

const PermissionedDeleteAction = withPermissions([[PERMISSION_OBJ_DOCUMENT_IMPORT, PERMISSION_DELETE]])(DeleteAction);

// Confirm text for Re-apply / Force re-parse is intentionally long
// (explains data-mutation impact). Without a cap, the Popconfirm overlay
// stretches to its content width and covers most of the table on wide
// screens. 360 px keeps the English/French wording to a few lines while
// leaving the row underneath visible.
const POPCONFIRM_OVERLAY_STYLE = { maxWidth: 360 };

// Review opens the editable drawer; submit there hits POST /document-imports/:id/retry
// (gated by Create on the backend). Hide the button from readonly users — otherwise
// they'd open a drawer they cannot submit from.
const ReviewButton = withPermissions([[PERMISSION_OBJ_DOCUMENT_IMPORT, PERMISSION_CREATE]])(
  ({ onClick }: { onClick: () => void }) => (
    <Tooltip title={getTranslatedString('waybills.imports.actions.review')}>
      <Button type="primary" shape="circle" icon={<FontAwesomeIcon icon={faEdit} />} onClick={onClick} />
    </Tooltip>
  ),
);

// Re-apply pushes the stored parsed_data back into the linked waybill without calling Gemini.
// Gated on Update (not Create) — no new import / attachment / OCR job is produced; the action
// only updates the linked waybill (mirrors the backend PermissionGuard on the endpoint).
const ReapplyButton = withPermissions([[PERMISSION_OBJ_DOCUMENT_IMPORT, PERMISSION_UPDATE]])(
  ({ onConfirm, loading }: { onConfirm: () => void; loading: boolean }) => (
    <Popconfirm
      title={getTranslatedString('waybills.imports.actions.reapply-confirm')}
      onConfirm={onConfirm}
      onCancel={(e) => e?.stopPropagation()}
      disabled={loading}
      overlayStyle={POPCONFIRM_OVERLAY_STYLE}
    >
      <Tooltip title={getTranslatedString('waybills.imports.actions.reapply')}>
        <Button
          shape="circle"
          icon={<FontAwesomeIcon icon={faSyncAlt} />}
          loading={loading}
          onClick={(e) => e.stopPropagation()}
        />
      </Tooltip>
    </Popconfirm>
  ),
);

// Distinct icon from the RetweetOutlined `reused` badge so both don't read as the same control.
const ForceReparseButton = withPermissions([[PERMISSION_OBJ_DOCUMENT_IMPORT, PERMISSION_CREATE]])(
  ({ onConfirm, confirmKey, loading }: { onConfirm: () => void; confirmKey: string; loading: boolean }) => (
    <Popconfirm
      title={getTranslatedString(confirmKey)}
      onConfirm={onConfirm}
      onCancel={(e) => e?.stopPropagation()}
      disabled={loading}
      overlayStyle={POPCONFIRM_OVERLAY_STYLE}
    >
      <Tooltip title={getTranslatedString('waybills.imports.actions.force-reparse')}>
        <Button
          type="primary"
          shape="circle"
          icon={<FontAwesomeIcon icon={faRedoAlt} />}
          loading={loading}
          onClick={(e) => e.stopPropagation()}
        />
      </Tooltip>
    </Popconfirm>
  ),
);

const onDelete = (id: number) => {
  api.documentImports
    .delete(id)
    .source.then(() => documentImportStore.deleteItem(id))
    .catch((err: any) => {
      // Network/timeout errors carry no response — axios surfaces them as
      // "timeout of 30000ms exceeded" or "Network Error", which scares the
      // operator without telling them whether the delete actually went
      // through server-side. Convert those into a friendly warning that
      // hints at the recovery action (refresh). Real HTTP errors (with a
      // response body) still go through the normal toast — those usually
      // carry an actionable server message.
      const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message ?? '');
      const isNetwork = !err?.response && !isTimeout;
      if (isTimeout || isNetwork) {
        notification.warning({
          message: getTranslatedString('waybills.imports.delete.unreachable'),
          duration: 8,
        });
        return;
      }
      showError(err);
    });
};

export interface ColumnsCallbacks {
  /** Removes a failed local upload row from the page-level state. The page
   *  owns the local list because client-only state shouldn't leak into the
   *  shared `documentImportStore`. */
  onRemoveLocal: (localId: string) => void;
  /** Re-sends the `File` kept on an `upload_failed` local row. */
  onRetryLocal: (localId: string) => void;
  /** Opens the Review drawer for a `review`-status server row. The drawer
   *  pre-fills from `parsedData` and POSTs edited values back through retry. */
  onReview: (row: cyrian.api.documentImports.DocumentImport) => void;
  /** Resubmits OCR on the existing S3 PDF as a new import row — fresh Gemini
   *  call; existing rows are kept and refreshed with the new result. Used when
   *  the stored `parsedData` itself is wrong (Gemini misread the PDF). Spends
   *  Gemini quota. */
  onForceReparse: (id: number) => void;
  /** Pushes the stored `parsedData` back into the linked waybill without
   *  calling Gemini. Used when the waybill drifted from the PDF (e.g. an
   *  Excel batch overwrote a field) and the operator wants the original OCR
   *  values back. Persisted-only — only that status has a linked waybill. */
  onReapply: (id: number) => void;
  /** Id of the row whose force-reparse POST is in flight (null when idle).
   *  Drives per-row spinner + disables Popconfirm to block double-click. */
  forceReparsingId: number | null;
  /** Id of the row whose re-apply POST is in flight (null when idle). Same
   *  loading semantics as `forceReparsingId`. */
  reapplyingId: number | null;
}

export const getColumns = ({
  onRemoveLocal,
  onRetryLocal,
  onReview,
  onForceReparse,
  onReapply,
  forceReparsingId,
  reapplyingId,
}: ColumnsCallbacks): ColumnsType<DisplayRow> => [
  {
    title: <FormattedMessage id="waybills.imports.column.file" />,
    dataIndex: 'fileName',
    key: 'fileName',
    ellipsis: true,
    render: (fileName: string, record: DisplayRow) => {
      const reusedFromId = !isLocalRow(record) ? record.reusedFromImportId : null;
      if (!reusedFromId) {
        return fileName;
      }
      // <span> (not <div>): antd wraps ellipsis cells in a <span>; a
      // block child would be invalid HTML5 here.
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Typography.Text ellipsis={{ tooltip: fileName }} style={{ flex: 1, minWidth: 0 }}>
            {fileName}
          </Typography.Text>
          <Tooltip title={getTranslatedString('waybills.imports.badge.reused', { id: reusedFromId })}>
            <RetweetOutlined />
          </Tooltip>
        </span>
      );
    },
  },
  {
    title: <FormattedMessage id="waybills.imports.column.status" />,
    key: 'status',
    render: (_: unknown, record: DisplayRow) => {
      if (isLocalRow(record)) {
        // queued: default grey dot (idle, not network-active)
        // uploading: processing (animated blue dot — POST in flight)
        // upload_failed: error (red dot, with the error message in the result col)
        const color: 'default' | 'processing' | 'error' =
          record._localStatus === 'queued' ? 'default' : record._localStatus === 'uploading' ? 'processing' : 'error';
        return (
          <Badge status={color} text={<FormattedMessage id={`waybills.imports.status.${record._localStatus}`} />} />
        );
      }
      return (
        <Badge
          status={STATUS_COLORS[record.status]}
          text={<FormattedMessage id={`waybills.imports.status.${record.status}`} />}
        />
      );
    },
  },
  {
    title: <FormattedMessage id="waybills.imports.column.created" />,
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 180,
    render: (_: unknown, record: DisplayRow) => {
      if (isLocalRow(record)) return '—';
      return record.createdAt ? new Date(record.createdAt).toLocaleString() : '';
    },
  },
  {
    title: <FormattedMessage id="waybills.imports.column.result" />,
    key: 'result',
    // Reserve fixed width and let the cell ellipsize. Without this, a long
    // error message renders wider than its column and overlaps the Actions
    // cell to the right.
    width: 360,
    ellipsis: { showTitle: false },
    render: (_: unknown, row: DisplayRow) => {
      if (isLocalRow(row)) {
        if (row._localStatus === 'upload_failed' && row._error) {
          return (
            <Tooltip title={row._error} placement="topLeft">
              <Typography.Text type="danger" ellipsis style={{ maxWidth: '100%' }}>
                {row._error}
              </Typography.Text>
            </Tooltip>
          );
        }
        return null;
      }
      if (row.status === 'persisted' && row.targetEntityId) {
        return (
          <UISref to="base-layout.waybills.view" params={{ waybillId: row.targetEntityId }}>
            <a onClick={(e) => e.stopPropagation()}>
              <FontAwesomeIcon icon={faExternalLinkAlt} />{' '}
              <FormattedMessage id="waybills.imports.result.waybill-link" values={{ id: row.targetEntityId }} />
            </a>
          </UISref>
        );
      }
      if (row.warnings && row.warnings.length > 0) {
        return (
          <Tooltip title={row.warnings.join('\n')}>
            <Tag color="orange">
              <FormattedMessage id="waybills.imports.result.warnings" values={{ count: row.warnings.length }} />
            </Tag>
          </Tooltip>
        );
      }
      if (row.error) {
        return (
          <Tooltip title={row.error} placement="topLeft">
            <Typography.Text type="danger" ellipsis style={{ maxWidth: '100%' }}>
              {row.error}
            </Typography.Text>
          </Tooltip>
        );
      }
      return null;
    },
  },
  {
    // Intentional divergence from the project-wide hover-revealed `.actions`
    // pattern (waybills/lots): this is a task queue, not a CRUD list, and
    // Retry/Delete are primary actions on each row, not secondary edit-tools.
    // CI/CD- and upload-queue UX precedents always keep these visible.
    title: <FormattedMessage id="waybills.imports.column.actions" />,
    key: 'actions',
    // 150px fits up to three 32px circle buttons + Space gaps (8px each).
    // The `review` status renders Review + ForceReparse + Delete simultaneously.
    width: 150,
    align: 'right',
    render: (_: unknown, record: DisplayRow) => (
      // The actions cell is its own interactive region — block row-level onClick
      // (none today, but matches the project-wide pattern in other tables).
      <Space onClick={(e) => e.stopPropagation()}>
        {isLocalRow(record) ? (
          // Remove is available on 'queued' (cancels before the POST fires —
          // cancellation signal flows through cancelledIdsRef on the page)
          // and on 'upload_failed' (just dismiss the row, there's no DB row
          // to delete). Mid-upload cancel is out of scope for v1.
          <>
            {record._localStatus === 'upload_failed' && record._file && (
              <Tooltip title={getTranslatedString('waybills.imports.actions.retry-upload')}>
                <Button
                  type="primary"
                  ghost
                  shape="circle"
                  aria-label={getTranslatedString('waybills.imports.actions.retry-upload')}
                  icon={<FontAwesomeIcon icon={faUpload} />}
                  onClick={() => onRetryLocal(record._localId)}
                />
              </Tooltip>
            )}
            {(record._localStatus === 'queued' || record._localStatus === 'upload_failed') && (
              <Tooltip title={getTranslatedString('waybills.imports.actions.remove')}>
                <Button
                  shape="circle"
                  icon={<FontAwesomeIcon icon={faTimes} />}
                  onClick={() => onRemoveLocal(record._localId)}
                />
              </Tooltip>
            )}
          </>
        ) : (
          <>
            {record.status === 'review' && <ReviewButton onClick={() => onReview(record)} />}
            {/* Re-apply: only persisted has a linked waybill + known-good parsedData to push. */}
            {record.status === 'persisted' && (
              <ReapplyButton onConfirm={() => onReapply(record.id)} loading={reapplyingId === record.id} />
            )}
            {/* Confirm text branches: only `persisted` has a waybill to overwrite. */}
            {(record.status === 'persisted' || record.status === 'review' || record.status === 'failed') && (
              <ForceReparseButton
                onConfirm={() => onForceReparse(record.id)}
                confirmKey={
                  record.status === 'persisted'
                    ? 'waybills.imports.actions.force-reparse-confirm-persisted'
                    : 'waybills.imports.actions.force-reparse-confirm-other'
                }
                loading={forceReparsingId === record.id}
              />
            )}
            {/* Delete is restricted to terminal states the operator can act on
                safely. 'pending' and 'parsed' mean OCR is still working on the
                row server-side; deleting then would race the worker (S3
                orphan, completion event hitting a missing row). 'persisted'
                means the document is already a real waybill — the operator
                should delete the waybill, not the import row. */}
            {(record.status === 'review' || record.status === 'failed') && (
              <PermissionedDeleteAction onConfirm={() => onDelete(record.id)} />
            )}
          </>
        )}
      </Space>
    ),
  },
];

// Convenience for non-render consumers (e.g. `getGenericFilters` introspects
// the column descriptors at module-load time and doesn't need any callbacks).
export const columns: ColumnsType<DisplayRow> = getColumns({
  onRemoveLocal: () => undefined,
  onRetryLocal: () => undefined,
  onReview: () => undefined,
  onForceReparse: () => undefined,
  onReapply: () => undefined,
  forceReparsingId: null,
  reapplyingId: null,
});
